import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  CalendarPlus,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useUpcomingEvents } from "@/hooks/useEvents";

interface SidebarHeaderProps {
  collapsed: boolean;
  onToggleCollapse?: () => void;
  /** Whether the current user may create an event (drives the empty-state action). */
  canCreate: boolean;
}

// Whole calendar days from today until the given date (negative if past).
function daysUntil(startDate: string): number {
  const start = new Date(startDate);
  const now = new Date();
  const startMid = Date.UTC(
    start.getFullYear(),
    start.getMonth(),
    start.getDate(),
  );
  const nowMid = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((startMid - nowMid) / 86_400_000);
}

const toggleButtonClass =
  "flex-shrink-0 rounded-lg p-2 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500";
const iconLinkClass =
  "relative flex-shrink-0 rounded-lg p-2 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500";
const cardClass =
  "group flex items-center gap-2.5 rounded-lg p-1.5 hover:bg-secondary-50";
const chipClass =
  "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600";

export default function SidebarHeader({
  collapsed,
  onToggleCollapse,
  canCreate,
}: SidebarHeaderProps) {
  const { t } = useTranslation("common");
  const { data, isLoading } = useUpcomingEvents();

  const events = data?.events ?? [];
  const nextEvent = events.length
    ? events.reduce((a, b) => (a.startDate <= b.startDate ? a : b))
    : undefined;

  const countdown = (startDate: string) => {
    const days = daysUntil(startDate);
    if (days <= 0) return t("navigation.eventToday");
    if (days === 1) return t("navigation.eventTomorrow");
    return t("navigation.eventInDays", { count: days });
  };

  const toggle = (
    <button onClick={onToggleCollapse} className={toggleButtonClass}>
      <span className="sr-only">
        {collapsed
          ? t("navigation.expandSidebar")
          : t("navigation.collapseSidebar")}
      </span>
      {collapsed ? (
        <PanelLeftOpen className="h-6 w-6" aria-hidden="true" />
      ) : (
        <PanelLeftClose className="h-6 w-6" aria-hidden="true" />
      )}
    </button>
  );

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1 border-b border-secondary-200 px-2 pb-3 pt-4">
        {toggle}
        {nextEvent ? (
          <Link
            to={`/events/${nextEvent.id}`}
            className={iconLinkClass}
            aria-label={nextEvent.name}
            title={nextEvent.name}
          >
            <Calendar className="h-5 w-5" aria-hidden="true" />
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary-500 ring-2 ring-white" />
          </Link>
        ) : canCreate ? (
          <Link
            to="/events/new"
            className={iconLinkClass}
            aria-label={t("navigation.scheduleEvent")}
            title={t("navigation.scheduleEvent")}
          >
            <CalendarPlus className="h-5 w-5" aria-hidden="true" />
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 border-b border-secondary-200 px-3 pb-3 pt-4">
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <div
            data-testid="next-event-loading"
            aria-hidden="true"
            className="flex items-center gap-2.5 p-1.5"
          >
            <div className="h-9 w-9 flex-shrink-0 animate-pulse rounded-lg bg-secondary-100" />
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="h-2.5 w-3/4 animate-pulse rounded bg-secondary-100" />
              <div className="h-2 w-1/2 animate-pulse rounded bg-secondary-100" />
            </div>
          </div>
        ) : nextEvent ? (
          <Link to={`/events/${nextEvent.id}`} className={cardClass}>
            <span className={chipClass}>
              <Calendar className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-semibold text-secondary-900">
                {nextEvent.name}
              </span>
              <span className="truncate text-xs text-secondary-400">
                {t("navigation.nextEvent")} · {countdown(nextEvent.startDate)}
              </span>
            </span>
          </Link>
        ) : canCreate ? (
          <Link to="/events/new" className={cardClass}>
            <span className={chipClass}>
              <CalendarPlus className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="truncate text-sm font-semibold text-secondary-900">
                {t("navigation.scheduleEvent")}
              </span>
              <span className="truncate text-xs text-secondary-400">
                {t("navigation.nothingUpcoming")}
              </span>
            </span>
          </Link>
        ) : (
          <span className="block px-1.5 text-sm text-secondary-400">
            {t("navigation.noUpcomingEvents")}
          </span>
        )}
      </div>
      {toggle}
    </div>
  );
}
